import { all, call, fork, put, takeLatest, takeEvery, select } from 'redux-saga/effects';

import { showErrorNotification, showSuccessNotification } from '../actions';
import * as actions from '../actions/Alarms';
import * as alarmsApi from '../api/Alarms';

import {
  GET_ACTIVE_ALARMS_COUNT,
  GET_ALARMS_HISTORY_LIST,
  GET_ALARMS_HISTORY_MESSAGES,
  GET_ALARM_LIST,
  GET_ALARM_MESSAGE_LIST,
  SEARCH_CALLS_HISTORY,
  SEND_ALARM_MESSAGE,
  START_ALARM_CALL,
  STOP_ALARM_CALL,
} from '../constants/ActionTypes';

import { CALL_SESSION_STATUSES } from '../constants/Values';

const activeAlarmsCountSelector = state => state.alarms.activeAlarmsCount;

const audioElement = document.createElement('audio');
audioElement.src = '../assets/alarm.wav';

function* getActiveAlarmsCount() {
  try {
    const activeAlarmsCount = yield select(activeAlarmsCountSelector);
    const { data } = yield call(alarmsApi.getActiveAlarmsCount);
    yield put(actions.getActiveAlarmsCountSuccess(data.count));
    if (
      activeAlarmsCount !== null &&
      activeAlarmsCount !== undefined &&
      activeAlarmsCount < data.count
    ) {
      audioElement.play();
    }
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  }
}

function* getAlarmHistoryList({ payload: { query } }) {
  try {
    const {
      data: { entities, pageCount },
    } = yield call(alarmsApi.getAlarmsHistory, query);
    yield put(actions.getAlarmsHistoryListSuccess(entities, pageCount));
  } catch (error) {
    yield put(actions.getAlarmsHistoryListError());
    yield put(showErrorNotification(error.messages));
  }
}

function* getAlarmHitoryMessages({ payload: { alarmHistoryId } }) {
  try {
    const { data } = yield call(alarmsApi.getAlarmHistoryDetails, alarmHistoryId);
    yield put(actions.getAlarmsHistoryMessagesSuccess(data.comments));
  } catch (error) {
    yield put(actions.getAlarmsHistoryMessagesError());
    yield put(showErrorNotification(error.messages));
  }
}

function* getAlarmList({ payload: { query } }) {
  try {
    const {
      data: { entities, pageCount },
    } = yield call(alarmsApi.getActiveAlarms, query);
    yield put(actions.getAlarmListSuccess(entities, pageCount));
  } catch (error) {
    yield put(actions.getAlarmListError());
    yield put(showErrorNotification(error.messages));
  }
}

function* getAlarmMessageList({ payload }) {
  try {
    const { data } = yield call(alarmsApi.getAlarmMessages, payload.alarmId);
    yield put(actions.getAlarmMessageListSuccess(data));
  } catch (error) {
    yield put(actions.getAlarmMessageListError());
    yield put(showErrorNotification(error.messages));
  }
}

function* searchCallsHistory({ payload: { query } }) {
  try {
    const {
      data: { entities, pageCount },
    } = yield call(alarmsApi.searchCallsHistory, query);
    yield put(actions.searchCallsHistorySuccess(entities, pageCount));
  } catch (error) {
    yield put(actions.searchCallsHistoryFailure());
    yield put(showErrorNotification(error.messages));
  }
}

function* sendAlarmMessage({ payload }) {
  try {
    yield call(alarmsApi.sendAlarmMessage, payload.alarmId, payload.message, payload.stopAlarm);
    yield put(actions.sendAlarmMessageSuccess());
    yield put(showSuccessNotification('modal.updateAlarms.messageSent'));

    if (payload.stopAlarm) {
      yield put(actions.getAlarmList(payload.query));
      yield put(actions.getActiveAlarmsCount());
    }
  } catch (error) {
    yield put(actions.sendAlarmMessageFailure());
    yield put(showErrorNotification(error.messages));
  }
}

function* startAlarmCall({ payload }) {
  const { alarmId } = payload;
  try {
    const { data } = yield call(alarmsApi.startAlarmCall, alarmId);

    yield put(
      actions.startAlarmCallSuccess({
        alarmId,
        responseStatus: data.status,
        directPhoneNumber: data.directPhoneNumber,
      }),
    );
    if (data.status !== CALL_SESSION_STATUSES.ALREADY_STARTED) {
      yield put(showSuccessNotification('alarms.call.stared'));
    }
  } catch (error) {
    yield put(actions.startAlarmCallError({ alarmId, error }));
    if (error && error.status === 403) {
      yield put(showErrorNotification(error.messages));
    } else {
      yield put(showErrorNotification('errors.startAlarmCall'));
    }
  }
}

function* stopAlarmCall({ payload }) {
  const { alarmId } = payload;
  try {
    yield call(alarmsApi.stopAlarmCall, alarmId);

    yield put(actions.stopAlarmCallSuccess({ alarmId }));
    yield put(showSuccessNotification('alarms.call.stop'));
  } catch (error) {
    yield put(actions.stopAlarmCallError({ alarmId, error }));
    yield put(showErrorNotification('errors.stopAlarmCall'));
  }
}

function* getActiveAlarmsCountSaga() {
  yield takeLatest(GET_ACTIVE_ALARMS_COUNT, getActiveAlarmsCount);
}

function* getAlarmHistoryListSaga() {
  yield takeLatest(GET_ALARMS_HISTORY_LIST, getAlarmHistoryList);
}

function* getAlarmHitoryMessagesSaga() {
  yield takeLatest(GET_ALARMS_HISTORY_MESSAGES, getAlarmHitoryMessages);
}

function* getAlarmListSaga() {
  yield takeLatest(GET_ALARM_LIST, getAlarmList);
}

function* getAlarmMessageListSaga() {
  yield takeLatest(GET_ALARM_MESSAGE_LIST, getAlarmMessageList);
}

function* searchCallsHistorySaga() {
  yield takeLatest(SEARCH_CALLS_HISTORY, searchCallsHistory);
}

function* sendAlarmMessageSaga() {
  yield takeLatest(SEND_ALARM_MESSAGE, sendAlarmMessage);
}

export function* startAlarmCallSaga() {
  yield takeEvery(START_ALARM_CALL, startAlarmCall);
}

export function* stopAlarmCallSaga() {
  yield takeEvery(STOP_ALARM_CALL, stopAlarmCall);
}

export default function* rootSaga() {
  yield all([
    fork(getActiveAlarmsCountSaga),
    fork(getAlarmHistoryListSaga),
    fork(getAlarmHitoryMessagesSaga),
    fork(getAlarmListSaga),
    fork(getAlarmMessageListSaga),
    fork(searchCallsHistorySaga),
    fork(sendAlarmMessageSaga),
    fork(startAlarmCallSaga),
    fork(stopAlarmCallSaga),
  ]);
}
